import "./polyfill";
import store from "./store";
import { runAfterNextRepaint } from "./utils";

export async function handlePromises(promises) {
  const results = await Promise.allSettled(promises);
  let errors = results
    .map((result, index) =>
      result.status === "rejected" ? `Promise ${index}: ${result.reason}` : null
    )
    .filter((error) => error !== null);
  if (errors.length > 0) {
    let combinedError = new Error(`Promise(s) failed: ${errors.join(", ")}`);
    combinedError.originalErrors = results
      .filter((result) => result.status === "rejected")
      .map((result) => result.reason);
    throw combinedError;
  }
}

const withTimeoutCb = (options, reject) => {
  if (!options.hasResolved) {
    options.hasResolved = true;
    const errorMsg = options.callerId
      ? `Timeout (${options.timeout}ms) exceeded by ${options.callerId}`
      : `Timeout (${options.timeout}ms) exceeded`;
    const error = new Error(errorMsg);
    error.type = "TIMEOUT";
    dispatchRequestTimeoutEvent(true);
    reject(error);
  }
};

const defaultTimeout = __DEV__ ? 3000 : 6000;

export async function withTimeout(
  promise,
  options = { timeout: defaultTimeout }
) {
  return new Promise((resolve, reject) => {
    options.hasResolved = false;
    const timeoutId = setTimeout(
      withTimeoutCb,
      options.timeout,
      options,
      reject
    );
    promise
      .then((res) => {
        if (!options.hasResolved) {
          options.hasResolved = true;
          dispatchRequestTimeoutEvent(false);
          resolve(res);
        }
      })
      .catch((error) => {
        if (!options.hasResolved) {
          options.hasResolved = true;
          reject(error);
        }
      })
      .finally(() => {
        clearTimeout(timeoutId);
      });
  });
}

function dispatchRequestTimeoutEvent(requestsTimingOut) {
  dispatchEvent(
    new CustomEvent("requestsTimingOutChange", {
      detail: { requestsTimingOut },
    })
  );
}

export async function withInternetConnected(promise) {
  return new Promise(async (resolve, reject) => {
    if (!store.internetConnected) {
      const error = new Error("Please check your internet connection 📡");
      error.type = "NO_INTERNET";
      reject(error);
    }
    try {
      const res = await promise;
      resolve(res);
    } catch (error) {
      reject(error);
    }
  });
}

const resolveCb = (resolve, cb, args) => resolve(cb(...args));

export const setTimeoutAsync = (cb, delay, ...args) =>
  new Promise((resolve) => setTimeout(resolveCb, delay, resolve, cb, args));

function resolveTimeout(resolve) {
  setTimeout(resolve);
}

function emptyCb() {}

export function yieldToMain() {
  return new Promise(resolveTimeout);
}

export function yieldWithPriority(priority, delay) {
  return window.scheduler.postTask(emptyCb, { priority, delay });
}

function resolveNextRepaint(resolve) {
  runAfterNextRepaint(resolve);
}

export function yieldNextRepaint() {
  return new Promise(resolveNextRepaint);
}

function resolveRepaint(resolve) {
  requestAnimationFrame(resolve);
}

export function yieldRepaint() {
  return new Promise(resolveRepaint);
}

export class PromiseExecutor {
  constructor() {
    if (this.constructor === PromiseExecutor)
      throw new Error(
        "Abstract class PromiseExecutor cannot be instantiated directly."
      );

    this._args = [];
  }

  _promiseCallback = (resolve, reject) => {
    throw new Error(
      "Abstract method '_promiseCallback' must be implemented in subclass"
    );
  };

  _schedulerCallback = () => {
    throw new Error(
      "Abstract method '_schedulerCallback' must be implemented in subclass"
    );
  };

  run(...args) {
    this._args = args;
    return new Promise(this._promiseCallback);
  }

  schedule(options, ...args) {
    this._args = args;
    return window.scheduler.postTask(this._schedulerCallback, options);
  }

  scheduleUserBlocking(...args) {
    return this.schedule({ priority: "user-blocking" }, ...args);
  }

  scheduleUserVisible(...args) {
    return this.schedule({ priority: "user-visible" }, ...args);
  }

  scheduleBackground(...args) {
    return this.schedule({ priority: "background" }, ...args);
  }
}

export const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
